import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Check } from 'lucide-react';
import FlowBuilder from './FlowBuilder';

const STORAGE_KEY = 'flowEditorMaximizedData';

const FlowEditorMaximizedPage = () => {
  const [flowData, setFlowData] = useState(null);
  const [requiredVariables, setRequiredVariables] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const flowBuilderRef = useRef(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFlowData(parsed.flowData || null);
        setRequiredVariables(parsed.requiredVariables || []);
      }
      setIsReady(true);
    } catch (e) {
      console.error('Failed to load flow data:', e);
      setIsReady(true);
    }
  }, []);

  const handleFlowChange = useCallback((flow) => {
    setFlowData(flow);
  }, []);

  const handleApplyAndClose = () => {
    try {
      // Read the canvas directly: onFlowChange is debounced, so an edit made
      // just before clicking Apply may not be in flowData yet
      const latestFlow = flowBuilderRef.current?.getCurrentFlow?.();
      const payload = {
        type: 'flow-editor-apply',
        flowData: latestFlow || flowData,
        requiredVariables,
      };
      if (window.opener) {
        window.opener.postMessage(payload, window.location.origin);
      }
      sessionStorage.removeItem(STORAGE_KEY);
      window.close();
    } catch (e) {
      console.error('Failed to apply flow:', e);
    }
  };

  const handleCancel = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    window.close();
  };

  if (!isReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading flow editor...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-border bg-card">
        <h1 className="text-xl font-bold text-foreground">Flow Editor</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
          >
            <X size={18} />
            Cancel
          </button>
          <button
            onClick={handleApplyAndClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
          >
            <Check size={18} />
            Apply & Close
          </button>
        </div>
      </header>

      {/* Flow Builder - full remaining height */}
      <div className="flex-1 min-h-0">
        <FlowBuilder
          ref={flowBuilderRef}
          flowData={flowData}
          onFlowChange={handleFlowChange}
          requiredVariables={requiredVariables}
        />
      </div>
    </div>
  );
};

export default FlowEditorMaximizedPage;
